/**
 * sim/combat/hitvolumes.ts — player hit volumes for missile and melee traces.
 *
 * OWNER: WEAPONS agent.
 *
 * T5/T6 missiles and melee use a LOCATIONAL point trace against the player's hitbox model, not the 30x30x70
 * movement hull (T5 G_MissileTrace -> G_LocationalTrace, g_missile.cpp:410; Melee_Trace ->
 * G_LocationalTraceAllowChildren, g_weapon.cpp:452). The BO2 hitbox bone sizes are not extracted yet
 * [NET §8.1 "ESTIMATE until the model/anim agent provides BO2 hitbox bone sizes"], so this is an ESTIMATE body:
 *   standing (eye 60): head sphere r 6.5 at z 63; torso capsule r 10 from z 34 to 52; legs capsule r 8 from
 *   z 5 to 30. Crouch scales the heights by viewHeight/60. Prone (viewHeight < 25) lays the same pieces along
 *   the body yaw. Hit locations follow the hit height / side (arms = the torso's outer 3 units).
 */
import { DEG2RAD, dcos, dsin, type Vec3 } from '@shared/math';
import { HitLoc } from '@shared/weapons/hitloc';

export interface PlayerPose {
  clientNum: number;
  alive: boolean;
  spawnCount: number;
  origin: Vec3;
  /** Eye height above origin (ps.viewHeightCurrent). */
  viewHeight: number;
  /** Body yaw, degrees (view yaw). */
  yaw: number;
}

export function createPose(clientNum = 0): PlayerPose {
  return { clientNum, alive: false, spawnCount: 0, origin: [0, 0, 0], viewHeight: 60, yaw: 0 };
}

export function copyPose(dst: PlayerPose, src: PlayerPose): PlayerPose {
  dst.clientNum = src.clientNum; dst.alive = src.alive; dst.spawnCount = src.spawnCount;
  dst.origin[0] = src.origin[0]; dst.origin[1] = src.origin[1]; dst.origin[2] = src.origin[2];
  dst.viewHeight = src.viewHeight; dst.yaw = src.yaw;
  return dst;
}

export interface VolumeHit {
  frac: number;
  normal: Vec3;
  point: Vec3;
  loc: number;
}

// ESTIMATE body dimensions (standing, inches)
const HEAD_R = 6.5;
const HEAD_ABOVE_EYE = 3;
const TORSO_R = 10;
const TORSO_Z0 = 34, TORSO_Z1 = 52;
const LEGS_R = 8;
const LEGS_Z0 = 5, LEGS_Z1 = 30;
const PRONE_VIEWHEIGHT = 25;

const a: Vec3 = [0, 0, 0];
const b: Vec3 = [0, 0, 0];
const c: Vec3 = [0, 0, 0];
const tmpN: Vec3 = [0, 0, 0];

/** Segment p0 + t*d (t in [0,1]) vs sphere; returns t or -1. Start inside -> 0. */
function segSphere(p0: Vec3, d: Vec3, cen: Vec3, r: number, nOut: Vec3): number {
  const ox = p0[0] - cen[0], oy = p0[1] - cen[1], oz = p0[2] - cen[2];
  const A = d[0] * d[0] + d[1] * d[1] + d[2] * d[2];
  const B = ox * d[0] + oy * d[1] + oz * d[2];
  const C = ox * ox + oy * oy + oz * oz - r * r;
  if (C <= 0) { nOut[0] = -d[0]; nOut[1] = -d[1]; nOut[2] = -d[2]; normalize(nOut); return 0; }
  if (A === 0) return -1;
  const h = B * B - A * C;
  if (h < 0) return -1;
  const t = (-B - Math.sqrt(h)) / A;
  if (t < 0 || t > 1) return -1;
  nOut[0] = (ox + t * d[0]) / r; nOut[1] = (oy + t * d[1]) / r; nOut[2] = (oz + t * d[2]) / r;
  return t;
}

/** Segment vs capsule (a, b, r) (iq's capsule intersection with sphere caps). */
function segCapsule(p0: Vec3, d: Vec3, pa: Vec3, pb: Vec3, r: number, nOut: Vec3): number {
  const bax = pb[0] - pa[0], bay = pb[1] - pa[1], baz = pb[2] - pa[2];
  const oax = p0[0] - pa[0], oay = p0[1] - pa[1], oaz = p0[2] - pa[2];
  const baba = bax * bax + bay * bay + baz * baz;
  const bard = bax * d[0] + bay * d[1] + baz * d[2];
  const baoa = bax * oax + bay * oay + baz * oaz;
  const rdoa = d[0] * oax + d[1] * oay + d[2] * oaz;
  const rdrd = d[0] * d[0] + d[1] * d[1] + d[2] * d[2];
  const oaoa = oax * oax + oay * oay + oaz * oaz;
  // start inside?
  let tAxis = baba > 0 ? baoa / baba : 0;
  tAxis = tAxis < 0 ? 0 : tAxis > 1 ? 1 : tAxis;
  const qx = oax - bax * tAxis, qy = oay - bay * tAxis, qz = oaz - baz * tAxis;
  if (qx * qx + qy * qy + qz * qz <= r * r) { nOut[0] = -d[0]; nOut[1] = -d[1]; nOut[2] = -d[2]; normalize(nOut); return 0; }
  let best = -1;
  const k2 = baba * rdrd - bard * bard;
  if (k2 !== 0) {
    const k1 = baba * rdoa - baoa * bard;
    const k0 = baba * oaoa - baoa * baoa - r * r * baba;
    const h = k1 * k1 - k2 * k0;
    if (h >= 0) {
      const t = (-k1 - Math.sqrt(h)) / k2;
      const y = baoa + t * bard;
      if (y > 0 && y < baba && t >= 0 && t <= 1) {
        best = t;
        const s = y / baba;
        nOut[0] = (oax + t * d[0] - bax * s) / r;
        nOut[1] = (oay + t * d[1] - bay * s) / r;
        nOut[2] = (oaz + t * d[2] - baz * s) / r;
      }
    }
  }
  const t1 = segSphere(p0, d, pa, r, tmpN);
  if (t1 >= 0 && (best < 0 || t1 < best)) { best = t1; nOut[0] = tmpN[0]; nOut[1] = tmpN[1]; nOut[2] = tmpN[2]; }
  const t2 = segSphere(p0, d, pb, r, tmpN);
  if (t2 >= 0 && (best < 0 || t2 < best)) { best = t2; nOut[0] = tmpN[0]; nOut[1] = tmpN[1]; nOut[2] = tmpN[2]; }
  return best;
}

function normalize(v: Vec3): void {
  const l = Math.sqrt(v[0] * v[0] + v[1] * v[1] + v[2] * v[2]);
  if (l > 0) { v[0] /= l; v[1] /= l; v[2] /= l; }
}

const n1: Vec3 = [0, 0, 0];
const d: Vec3 = [0, 0, 0];

/**
 * Point-trace the segment start->end against one pose. Returns true and fills `out` (fraction, outward normal,
 * point, hit location) for the nearest piece.
 */
export function traceHitVolumes(start: Vec3, end: Vec3, pose: PlayerPose, out: VolumeHit): boolean {
  if (!pose.alive) return false;
  d[0] = end[0] - start[0]; d[1] = end[1] - start[1]; d[2] = end[2] - start[2];
  const o = pose.origin;
  // quick reject: bounding sphere around the body
  const midZ = o[2] + 36;
  {
    const ox = start[0] - o[0], oy = start[1] - o[1], oz = start[2] - midZ;
    const A = d[0] * d[0] + d[1] * d[1] + d[2] * d[2];
    const B = ox * d[0] + oy * d[1] + oz * d[2];
    let t = A > 0 ? -B / A : 0;
    t = t < 0 ? 0 : t > 1 ? 1 : t;
    const px = ox + d[0] * t, py = oy + d[1] * t, pz = oz + d[2] * t;
    if (px * px + py * py + pz * pz > 60 * 60) return false;
  }
  const yr = pose.yaw * DEG2RAD;
  const fx = dcos(yr), fy = dsin(yr);
  let best = -1;
  let piece = 0;
  if (pose.viewHeight < PRONE_VIEWHEIGHT) {
    // prone: head forward, legs back, along the body yaw
    c[0] = o[0] + fx * 22; c[1] = o[1] + fy * 22; c[2] = o[2] + 10;
    let t = segSphere(start, d, c, HEAD_R, n1);
    if (t >= 0) { best = t; piece = 1; out.normal[0] = n1[0]; out.normal[1] = n1[1]; out.normal[2] = n1[2]; }
    a[0] = o[0] + fx * 12; a[1] = o[1] + fy * 12; a[2] = o[2] + 8;
    b[0] = o[0] - fx * 8; b[1] = o[1] - fy * 8; b[2] = o[2] + 8;
    t = segCapsule(start, d, a, b, 9, n1);
    if (t >= 0 && (best < 0 || t < best)) { best = t; piece = 2; out.normal[0] = n1[0]; out.normal[1] = n1[1]; out.normal[2] = n1[2]; }
    a[0] = o[0] - fx * 10; a[1] = o[1] - fy * 10; a[2] = o[2] + 6;
    b[0] = o[0] - fx * 38; b[1] = o[1] - fy * 38; b[2] = o[2] + 5;
    t = segCapsule(start, d, a, b, 7, n1);
    if (t >= 0 && (best < 0 || t < best)) { best = t; piece = 3; out.normal[0] = n1[0]; out.normal[1] = n1[1]; out.normal[2] = n1[2]; }
  } else {
    const s = pose.viewHeight / 60;
    c[0] = o[0]; c[1] = o[1]; c[2] = o[2] + pose.viewHeight + HEAD_ABOVE_EYE * s;
    let t = segSphere(start, d, c, HEAD_R, n1);
    if (t >= 0) { best = t; piece = 1; out.normal[0] = n1[0]; out.normal[1] = n1[1]; out.normal[2] = n1[2]; }
    a[0] = o[0]; a[1] = o[1]; a[2] = o[2] + TORSO_Z0 * s;
    b[0] = o[0]; b[1] = o[1]; b[2] = o[2] + TORSO_Z1 * s;
    t = segCapsule(start, d, a, b, TORSO_R, n1);
    if (t >= 0 && (best < 0 || t < best)) { best = t; piece = 2; out.normal[0] = n1[0]; out.normal[1] = n1[1]; out.normal[2] = n1[2]; }
    a[2] = o[2] + LEGS_Z0; b[2] = o[2] + LEGS_Z1 * s;
    t = segCapsule(start, d, a, b, LEGS_R, n1);
    if (t >= 0 && (best < 0 || t < best)) { best = t; piece = 3; out.normal[0] = n1[0]; out.normal[1] = n1[1]; out.normal[2] = n1[2]; }
  }
  if (best < 0) return false;
  out.frac = best;
  out.point[0] = start[0] + d[0] * best; out.point[1] = start[1] + d[1] * best; out.point[2] = start[2] + d[2] * best;
  out.loc = classify(pose, piece, out.point, fx, fy);
  return true;
}

/** Hit location from the piece, height and side (ESTIMATE bands). */
function classify(pose: PlayerPose, piece: number, p: Vec3, fx: number, fy: number): number {
  const o = pose.origin;
  const rx = p[0] - o[0], ry = p[1] - o[1];
  // right vector = (fy, -fx) in CoD space (yaw 0 faces +X, right is -Y)
  const side = rx * fy - ry * fx; // > 0: right
  const right = side > 0;
  if (piece === 1) {
    if (pose.viewHeight >= PRONE_VIEWHEIGHT) {
      const topZ = o[2] + pose.viewHeight + HEAD_ABOVE_EYE * (pose.viewHeight / 60);
      return p[2] > topZ + 4 ? HitLoc.HELMET : HitLoc.HEAD;
    }
    return HitLoc.HEAD;
  }
  if (pose.viewHeight < PRONE_VIEWHEIGHT) {
    const along = rx * fx + ry * fy; // + toward head
    if (piece === 2) return along > 6 ? HitLoc.TORSO_UPPER : along > -2 ? HitLoc.TORSO_MID : HitLoc.TORSO_LOWER;
    if (along > -22) return right ? HitLoc.R_LEG_UPPER : HitLoc.L_LEG_UPPER;
    if (along > -33) return right ? HitLoc.R_LEG_LOWER : HitLoc.L_LEG_LOWER;
    return right ? HitLoc.R_FOOT : HitLoc.L_FOOT;
  }
  const s = pose.viewHeight / 60;
  const z = (p[2] - o[2]) / s;
  if (piece === 2) {
    if (z >= 55) return HitLoc.NECK;
    if (Math.abs(side) > 7 && z > 36) {
      if (z > 44) return right ? HitLoc.R_ARM_UPPER : HitLoc.L_ARM_UPPER;
      return right ? HitLoc.R_ARM_LOWER : HitLoc.L_ARM_LOWER;
    }
    if (z >= 45) return HitLoc.TORSO_UPPER;
    if (z >= 38) return HitLoc.TORSO_MID;
    return HitLoc.TORSO_LOWER;
  }
  if (z >= 18) return right ? HitLoc.R_LEG_UPPER : HitLoc.L_LEG_UPPER;
  if (z >= 6) return right ? HitLoc.R_LEG_LOWER : HitLoc.L_LEG_LOWER;
  return right ? HitLoc.R_FOOT : HitLoc.L_FOOT;
}
